/**
 * Plain-English summaries of adjustment rules — Ani's v0 step 5.
 *
 * Her mockup shows each rule as a sentence you can check against the contract ("payment = MIN(billed
 * charges, contracted rate) − member responsibility") rather than a form of typed fields. That is the
 * right review affordance: the question at this step is *does this rule match what the contract says*,
 * which you answer by reading it, not by inspecting `multiplier: 0.85`.
 *
 * **The logic is DERIVED, never authored.** It is generated from the construct's own config, so it
 * cannot drift from what will actually be built, and there is no second source of truth to keep in
 * sync. Editing still happens in the typed form — this is the reading surface.
 */

import { IDraftRecommendation } from "../../../store/queries/contractModelDrafts";

const modeling = (rec: IDraftRecommendation): Record<string, unknown> =>
  (rec.proposedModeling ?? {}) as Record<string, unknown>;

const num = (v: unknown): number | null => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return null;
};

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim().length > 0 ? v.trim() : null;

const codes = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((c): c is string => typeof c === "string") : [];

const money = (cents: number): string =>
  `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** A ruleset multiplier is a decimal fraction; 0.85 reads as 85% to a human. */
const asPercent = (multiplier: number): string =>
  `${Math.round(multiplier * 1000) / 10}%`;

const SCOPE_WORDS: Record<string, string> = {
  LINE_CHARGES: "each line's charges",
  TOTAL_CHARGES: "the claim's total charges",
};

/**
 * What the rule does, in a sentence.
 *
 * Falls back to the construct's own name rather than inventing prose when a rule carries no config we
 * can read — a confidently wrong sentence about how money is calculated is worse than an honest
 * "we can't summarise this one".
 */
export const describeRuleLogic = (rec: IDraftRecommendation): string => {
  const m = modeling(rec);
  const scope = SCOPE_WORDS[str(m.scope) ?? ""] ?? "each line's charges";
  const multiplier = num(m.multiplier);
  const maxAmount = num(m.maxAmountInCents);

  switch (rec.componentType) {
    case "lesser_of_charges": {
      const pct = multiplier != null ? asPercent(multiplier) : "the contracted";
      return `Pay the LESSER of ${pct} of ${scope} and the contracted rate.`;
    }
    case "charge_based_pricing": {
      const pct = multiplier != null ? asPercent(multiplier) : "a percentage";
      const cap = maxAmount != null ? `, capped at ${money(maxAmount)}` : "";
      return `Pay ${pct} of ${scope}${cap}.`;
    }
    case "mpdr_ruleset": {
      const ranks = [
        ["2nd", num(m.discountPercentForRank2)],
        ["3rd", num(m.discountPercentForRank3)],
        ["4th", num(m.discountPercentForRank4)],
        ["5th+", num(m.discountPercentForRank5Plus)],
      ].filter(([, v]) => v != null) as [string, number][];
      if (ranks.length === 0)
        return "Reduce payment on additional procedures in the same session.";
      const parts = ranks.map(([label, v]) => `${label} pays ${v}%`);
      return `When several procedures are billed together, the highest-priced pays in full; ${parts.join(
        ", "
      )}.`;
    }
    case "case_rate_ruleset": {
      const isPerDiem = m.isPerDiem === true;
      const n = codes(m.cptCodes).length;
      const codeText = n > 0 ? ` across ${n} code${n === 1 ? "" : "s"}` : "";
      return isPerDiem
        ? `Pay a per-diem rate for each day${codeText}, instead of pricing the lines individually.`
        : `Pay a single case rate${codeText}, instead of pricing the lines individually.`;
    }
    case "package_ruleset": {
      const triggers = codes(m.triggerCptCodes).length;
      const bundled = codes(m.bundledCptCodes).length;
      return `When any of ${triggers} trigger code${
        triggers === 1 ? "" : "s"
      } is billed, fold ${bundled} bundled code${
        bundled === 1 ? "" : "s"
      } into it and do not pay them separately.`;
    }
    case "exploding_fee_schedule": {
      const total = num(m.totalRateInCents);
      const n = codes(m.cptCodes).length;
      return total != null
        ? `Pay ${money(
            total
          )} in total across these ${n} codes, however they are billed.`
        : `Pay one total across these ${n} codes, however they are billed.`;
    }
    case "unit_cap_ruleset": {
      const max = num(m.maxUnitsPerCode);
      return max != null
        ? `Pay at most ${max} unit${max === 1 ? "" : "s"} per code, per claim.`
        : "Cap the units payable per code.";
    }
    default:
      return str(m.note) ?? "No summary available for this rule type.";
  }
};

/**
 * What the rule is scoped to — v0's "Applies to" column. Prefers an explicit code list, because that is
 * the answer the reviewer is checking; falls back to the declared scope, then to the whole contract.
 */
export const ruleAppliesTo = (rec: IDraftRecommendation): string => {
  const m = modeling(rec);
  const cpt = codes(m.cptCodes);
  const triggers = codes(m.triggerCptCodes);
  const list = cpt.length > 0 ? cpt : triggers;

  if (list.length > 0) {
    const shown = list.slice(0, 4).join(", ");
    const rest = list.length - Math.min(4, list.length);
    return rest > 0 ? `${shown} and ${rest} more` : shown;
  }
  const groupName = str(m.groupName);
  if (groupName) return groupName;
  const scope = SCOPE_WORDS[str(m.scope) ?? ""];
  if (scope) return `All rate lines — ${scope}`;
  return "All rate lines";
};

/**
 * Where the rule came from. Prefers a real document locator over the agent's prose: "Exhibit B, Page 4"
 * is checkable against the contract in a way that a rationale sentence is not.
 */
export const ruleSource = (rec: IDraftRecommendation): string | null => {
  const withLocator = rec.attributions?.find((a) => !!a.locator);
  if (withLocator?.locator) return withLocator.locator;
  return str(modeling(rec).contractLanguage) ?? null;
};

/** v0 shows a short type chip next to each rule title ("Lesser Of", "Bundling", "Carve-Out"). */
export const ruleKindLabel = (rec: IDraftRecommendation): string => {
  switch (rec.componentType) {
    case "lesser_of_charges":
      return "Lesser Of";
    case "charge_based_pricing":
      return "% of Charges";
    case "mpdr_ruleset":
      return "Multiple Procedure";
    case "case_rate_ruleset":
      return modeling(rec).isPerDiem === true ? "Per Diem" : "Case Rate";
    case "package_ruleset":
      return "Bundling";
    case "exploding_fee_schedule":
      return "Exploding Fee";
    case "unit_cap_ruleset":
      return "Unit Cap";
    default:
      return rec.componentType.replace(/_/g, " ");
  }
};

/** The rule's headline — its group name where it has one, else the kind. */
export const ruleTitle = (rec: IDraftRecommendation): string =>
  str(modeling(rec).groupName) ?? str(modeling(rec).name) ?? ruleKindLabel(rec);
